import {ChatMessage, ChatMessages, MessageFactory} from "@/app-modules/messages/chat/types";
import {Action, ListItemChange, OnListItemsListener} from "@/shared/types";
import {BaseFragment, BaseFragmentState} from "@/shared/BaseFragment";
import React, {ReactElement} from "react";
import {StyledBoxRow} from "@/shared/StyledComponents";
import {Button, CircularProgress, Typography} from "@mui/material";
import {PD_MD} from "@/shared/dimens";
import {BaseApp} from "../../shared/BaseApp";
import {AskWyzeAgent} from "./AskWyzeAgent";
import {ChatThreadFragment, ChatThreadFragmentProps} from "../../app-modules/messages/chat/ChatThreadFragment";
import {AskWyzeParticipantKey} from "./types";
import {AddOutlined} from "@mui/icons-material";
import {Debouncer} from "../../shared/file_util";

export type AskWyzeMessagesFragmentProps = ChatThreadFragmentProps & {
  practiceId: string,
  onNewChat: () => void,
}

export type AskWyzeMessagesFragmentState = BaseFragmentState & {
  messages: ChatMessage[],
  chatBarElement?: ReactElement,
  chatBarStatus?: ReactElement,
  audioPlaying?: boolean,
}

export class AskWyzeMessagesFragment extends BaseFragment<AskWyzeMessagesFragmentProps, AskWyzeMessagesFragmentState> implements OnListItemsListener<ChatMessage> {

  private static readonly DEBOUNCER = new Debouncer(1000);
  private readonly askWyzeParticipantKey = AskWyzeParticipantKey(this.props.practiceId);

  private readonly audio = BaseApp.CONTEXT.getAppRefs<any>().audio;
  private readonly agent: AskWyzeAgent = new AskWyzeAgent(this.props.myParticipantKey);

  private messages: ChatMessages;

  protected onCreateState(): AskWyzeMessagesFragmentState {
    return {
      ...super.onCreateState(),
      messages: [],
    };
  }

  componentDidUpdate(prevProps: Readonly<AskWyzeMessagesFragmentProps>, prevState: Readonly<AskWyzeMessagesFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.messages.length !== this.state.messages.length && this.state.messages[this.state.messages.length - 1]?.from.id === this.props.myParticipantKey.id) {
      this.setState({
        chatBarElement: <StyledBoxRow style={{
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center"
        }}><CircularProgress/><Typography>Thinking...</Typography></StyledBoxRow>
      })
      AskWyzeMessagesFragment.DEBOUNCER.run(() => {
        this.generateChat()
          .finally(() => this.setState({
            chatBarElement: null,
          }));
      });
    }
    if (prevState.audioPlaying !== this.state.audioPlaying) {
      this.setState({
        chatBarStatus: this.state.audioPlaying
          ? <StyledBoxRow style={{padding: PD_MD, alignItems: "center", justifyContent: "center"}}>
            <Typography>Speaking...</Typography>
            <Button
              variant="contained"
              onClick={() => this.audio.pause()}>
              Stop
            </Button>
          </StyledBoxRow>
          : null,
      })
    }
  }

  private async generateChat() {
    const resp = await this.agent.chat(this.state.messages);//.filter(message => message.from.id === this.props.myParticipantKey.id || message.from.id === this.props.personaType));
    // await this.playText(resp);
    await this.messages.addListItem(MessageFactory.createTextMessage(this.props.thread.id, this.askWyzeParticipantKey, resp));
  };

  private async playText(text: string) {
    const encodedFile = await this.agent.textToSpeech(text);
    this.audio.src = "data:audio/mp3;base64," + encodedFile;
    this.audio.play();
  }

  componentDidMount() {
    // We need to add this here, as sometimes we get the mount -> unmount -> mount sequence, so we need to re-register
    // with messages.
    this.messages?.registerObserver(this);
    super.componentDidMount();
    this.audio.addEventListener("play", this.onPlayStateChanged.bind(this));
    this.audio.addEventListener("pause", this.onPlayStateChanged.bind(this));
    this.audio.addEventListener("ended", this.onPlayStateChanged.bind(this));
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.messages?.unregisterObserver(this);
    this.audio.removeEventListener("play", this.onPlayStateChanged.bind(this));
    this.audio.removeEventListener("pause", this.onPlayStateChanged.bind(this));
    this.audio.removeEventListener("ended", this.onPlayStateChanged.bind(this));
  }

  onPlayStateChanged(): void {
    this.setState({
      audioPlaying: !this.audio.paused,
    });
  }

  protected async fetchOnMount(): Promise<void> {
    if (this.messages) {
      this.messages.unregisterObserver(this);
    }
    this.messages = ChatMessages.getInstance(this.props.myParticipantKey.type, this.props.thread.id);
    this.setState({
      messages: await this.messages.getOrLoadListItems(),
    });
    this.messages.registerObserver(this);
  }

  onItemChanged(item: ChatMessage, change: ListItemChange): void {
    if (change === ListItemChange.ADDED && item.from.id === this.props.myParticipantKey.id) {
      this.setState({
        messages: this.messages.getListItems(),
      });
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    return <ChatThreadFragment
      toolbarActions={[
        new Action("New Chat", () => this.props.onNewChat(), AddOutlined).setVariant("contained"),
      ]}
      chatBarElement={this.state.chatBarElement}
      chatBarStatus={this.state.chatBarStatus}
      // chatBarConfig={{
      //   showVoiceInputButton: true,
      //   voiceAutoSend: AppPrefs.getInstance().getAutosendChat(),
      //   onVoiceInputToText: url => this.agent.speechToText(url)
      // }}
      {...this.props}/>;
  }
}